import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Divider,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Select,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Skeleton
} from '@mui/material';

import {
  AddOutlined as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  DescriptionOutlined as DescriptionIcon, // Default/General
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  North as NorthIcon,
  South as SouthIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useHeaderContext } from '../contexts/HeaderContext'; // Assuming path
import { useNavigate } from 'react-router-dom';
import { useContentResponsive } from '@/contexts/ContentSpaceContext';

// Components
import { UnifiedTemplateCreationDialog } from '@/components/UnifiedTemplateCreation';

// Services (ensure these paths are correct)
import {
  // UpdateTemplateInput, // Add if update functionality is also implemented on this page
  getAllTemplates,
  createTemplate,
  deleteTemplate,
  duplicateTemplate
} from '@/services/templateService'; // Adjust path
import { useAppUser } from '@/contexts/AppUserProvider';
import { CreateTemplateInput, Template } from '@/types/templateTypes';
import { TemplateCategory } from '@/types/templateCategoryTypes';
import { getAllTemplateCategories } from '@/services/templateCategoryService';
import {
  SortIconDropdown,
  SortOption
} from '@/components/SortIconDropdown/SortIconDropdown';
import { EmptyState } from '@/components/EmptyState';
import { FileText, Article } from '@phosphor-icons/react';

// Helper to format dates (you might have a utility for this)
const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return dateString; // fallback
  }
};

export const TemplatesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { shouldStackVertically, getGridSizes } = useContentResponsive();
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const navigate = useNavigate();
  const {
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    isLoading: isAuthLoading
  } = useAuth0();

  const { appUser } = useAppUser();

  // Data states
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateCategories, setTemplateCategories] = useState<TemplateCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  type StatusFilter = 'all' | 'active' | 'archived';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  type SortBy = 'updatedAt' | 'name' | 'createdAt';
  const [sortBy, setSortBy] = useState<SortBy>('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Sort options for the new dropdown
  const sortOptions: SortOption[] = [
    { key: 'updatedAt', label: 'Updated' },
    { key: 'name', label: 'Name' },
    { key: 'createdAt', label: 'Created' }
  ];
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false); // Added for delete dialog
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null
  ); // Added for delete dialog

  // Actions menu state for list view
  const [templateActionMenuAnchorEl, setTemplateActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedTemplateForAction, setSelectedTemplateForAction] =
    useState<Template | null>(null);
  

  // Loading and Error states
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [errorTemplates, setErrorTemplates] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null); // Added for delete error feedback
  const [duplicatingTemplateId, setDuplicatingTemplateId] = useState<
    number | null
  >(null); // Track which template is being duplicated

  const userId = appUser?.id; // Assuming appUser has the userId

  // Fetch Template Categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!userId || isAuthLoading) return;
      setIsLoadingCategories(true);
      try {
        const categories = await getAllTemplateCategories(
          userId,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        setTemplateCategories(categories);
      } catch (err: any) {
        console.error('Failed to load template categories:', err);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    if (appUser) {
      fetchCategories();
    }
  }, [appUser, userId, isAuthLoading, getAccessTokenSilently, getAccessTokenWithPopup]);

  // Fetch Templates
  const fetchTemplates = useCallback(async () => {
    if (!userId || isAuthLoading) return;
    setIsLoadingTemplates(true);
    setErrorTemplates(null);
    try {
      const params = {
        userId,
        includeArchived: statusFilter !== 'active'
      };
      const fetchedTemplates = await getAllTemplates(
        params,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      const processed =
        statusFilter === 'archived'
          ? fetchedTemplates.filter((t) => t.isArchived)
          : fetchedTemplates;
      setTemplates(processed);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setErrorTemplates(
        err instanceof Error ? err.message : 'Failed to load templates.'
      );
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [
    userId,
    statusFilter,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    isAuthLoading
  ]);

  useEffect(() => {
    if (userId) {
      fetchTemplates();
    }
  }, [userId, fetchTemplates]);

  useEffect(() => {
    setHeaderText(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Article size={16} weight="regular" />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Templates
        </Typography>
      </Box>
    );
    setMenuActions(
      isMobile ? (
        <IconButton
          onClick={() => setIsTemplateDialogOpen(true)}
          disabled={false}
          sx={{
            backgroundColor: '#e6f226',
            color: '#030766',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#d4e022'
            },
            '&:disabled': {
              backgroundColor: 'rgba(230, 242, 38, 0.3)',
              color: 'rgba(3, 7, 102, 0.4)'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsTemplateDialogOpen(true)}
          disabled={false}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: '#e6f226',
            color: '#030766',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#d4e022',
              boxShadow: 'none'
            },
            '&:disabled': {
              backgroundColor: 'rgba(230, 242, 38, 0.3)',
              color: 'rgba(3, 7, 102, 0.4)'
            }
          }}
        >
          New Template
        </Button>
      )
    );
    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [
    setHeaderText,
    setMenuActions,
    theme.palette.mode,
    isMobile
  ]);

  const applySort = useCallback(
    (list: Template[]) => {
      const arr = [...list];
      arr.sort((a, b) => {
        let aVal: string | number = 0;
        let bVal: string | number = 0;
        if (sortBy === 'name') {
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
        } else if (sortBy === 'updatedAt') {
          aVal = new Date(a.updatedAtUtc).getTime();
          bVal = new Date(b.updatedAtUtc).getTime();
        } else {
          aVal = new Date(a.createdAtUtc).getTime();
          bVal = new Date(b.createdAtUtc).getTime();
        }
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return arr;
    },
    [sortBy, sortDir]
  );

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return templates.filter((template) => {
      // Search filter
      const name = template.name.toLowerCase();
      const desc = (template.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [templates, searchQuery]);

  const displayedTemplates = useMemo(
    () => applySort(filteredTemplates),
    [filteredTemplates, applySort]
  );

  const handleCreateTemplate = async (
    name: string,
    description: string,
    selectedCategoryId: number | null,
    titlePrompt?: string
  ) => {
    if (!userId || isAuthLoading) {
      console.error('User not available for creating template.');
      return;
    }

    const input: CreateTemplateInput = {
      name,
      description,
      templateCategoryId: selectedCategoryId || 1, // Use default category ID if not provided
      titleGenerationPrompt: titlePrompt
    };

    try {
      const newTemplate = await createTemplate(
        input,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setTemplates((prev) => [newTemplate, ...prev]);
      setIsTemplateDialogOpen(false);
      navigate(
        `/templates/${newTemplate.id}/${encodeURIComponent(
          newTemplate.name
        )}/edit`
      );
    } catch (error) {
      console.error('Failed to create template:', error);
      alert(
        `Failed to create template: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  };

  const handleOpenDeleteDialog = (template: Template) => {
    // Modified
    setTemplateToDelete(template);
    setOpenDeleteConfirmDialog(true);
    setDeleteError(null); // Clear previous errors
  };

  const handleCloseDeleteDialog = () => {
    // Added
    setOpenDeleteConfirmDialog(false);
    setTemplateToDelete(null);
  };

  const handleOpenTemplateActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    template: Template
  ) => {
    setTemplateActionMenuAnchorEl(event.currentTarget);
    setSelectedTemplateForAction(template);
  };

  const handleCloseTemplateActionMenu = () => {
    setTemplateActionMenuAnchorEl(null);
    setSelectedTemplateForAction(null);
  };

  const handleConfirmDeleteTemplate = async () => {
    // Added
    if (!userId || !templateToDelete) return;

    try {
      const success = await deleteTemplate(
        templateToDelete.id,
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      if (success) {
        setTemplates((prev) =>
          prev.filter((t) => t.id !== templateToDelete.id)
        );
        // Optionally: show a success Snackbar/Toast here
      } else {
        setDeleteError(
          'Could not delete template. It might have already been removed or an error occurred.'
        );
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
      setDeleteError(
        `Failed to delete template: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    if (!userId) return;

    setDuplicatingTemplateId(template.id);

    try {
      const duplicatedTemplate = await duplicateTemplate(
        template.id,
        undefined, // Let the service generate the smart name
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Add the new template to the list
      setTemplates((prev) => [duplicatedTemplate, ...prev]);

      // Navigate to the edit page of the new template
      navigate(
        `/templates/${duplicatedTemplate.id}/${encodeURIComponent(
          duplicatedTemplate.name
        )}/edit`
      );
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      // TODO: Add error handling UI (toast/snackbar)
      alert(
        `Failed to duplicate template: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setDuplicatingTemplateId(null);
    }
  };

  const isLoading = isLoadingTemplates || isAuthLoading;

  const isInitialLoading = isLoading && !templates.length;

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: `${theme.palette.background.default} !important`
      }}
    >
      {/* Search and Filter Controls Section */}
      <Box
        sx={{
          background: theme.palette.background.paper,
          px: isMobile ? 2 : 3,
          pt: 2,
          pb: 0
        }}
      >
        {/* Search and Sort Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2
          }}
        >
          <TextField
            placeholder="Search templates..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                height: 40,
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                },
                '&.Mui-focused': {
                  borderColor: '#6b7280',
                  boxShadow: 'none'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                </InputAdornment>
              )
            }}
          />

          {!isMobile && (
            <>
              <SortIconDropdown
                options={sortOptions}
                currentSort={sortBy}
                currentDirection={sortDir}
                onSortChange={(key, direction) => {
                  if (key && direction) {
                    setSortBy(key as SortBy);
                    setSortDir(direction);
                  }
                }}
                size="small"
              />

              <Button
                onClick={() => {
                  setShowFilters(!showFilters);
                }}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              height: 40,
              width: 40,
              minWidth: 40,
              px: 0,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: showFilters
                ? 'rgba(75, 94, 219, 0.04)'
                : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white'),
              color: showFilters ? '#4B5EDB' : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: showFilters
                  ? 'rgba(75, 94, 219, 0.08)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f8f9fa'),
                borderColor: showFilters ? '#4B5EDB' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db')
              }
            }}
          >
            <FilterListIcon sx={{ fontSize: '18px' }} />
          </Button>
            </>
          )}
        </Box>

      </Box>

      {/* Main Content Section */}
      <Box
        sx={{
          px: isMobile ? 2 : 3,
          pt: 0,
          pb: 3,
          flex: 1,
          overflow: 'auto',
          background: theme.palette.background.paper
        }}
      >
        {errorTemplates && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorTemplates}
          </Alert>
        )}
        {deleteError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setDeleteError(null)}
          >
            {deleteError}
          </Alert>
        )}

        {isInitialLoading ? (
          <Stack spacing={1}>
            {[1, 2, 3, 4].map((i) => (
              <Card
                key={i}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <Skeleton 
                  variant="rounded" 
                  width={32} 
                  height={32} 
                  sx={{ 
                    borderRadius: '4px',
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton 
                    width="45%" 
                    height={20} 
                    sx={{ 
                      mb: 0.5,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(0,0,0,0.06)'
                    }} 
                  />
                  <Skeleton 
                    width="30%" 
                    height={16} 
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(0,0,0,0.06)'
                    }} 
                  />
                </Box>
              </Card>
            ))}
          </Stack>
        ) : displayedTemplates.length > 0 ? (
          <Stack spacing={1}>
            {displayedTemplates.map((template, index) => (
              <Card
                key={template.id}
                onClick={() => {
                  navigate(
                    `/templates/${template.id}/${encodeURIComponent(
                      template.name
                    )}/edit`
                  );
                }}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  opacity: 0,
                  animation: 'fadeSlideIn 0.3s ease forwards',
                  animationDelay: `${index * 30}ms`,
                  '@keyframes fadeSlideIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  },
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fafafa'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease'
                  }
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2
                  }}
                >
                  {/* Left Side - Template Info */}
                  <Box
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 1.5,
                      flex: 1,
                      minWidth: 0
                    }}
                  >

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Template Title */}
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          textAlign: 'left',
                          mb: 0.5,
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {template.name}
                      </Typography>

                      {/* Metadata Row */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: theme.palette.text.secondary
                          }}
                        >
                          {template.questionCount || 0} questions
                        </Typography>
                        
                        {/* Hide description on mobile to keep cards compact */}
                        {template.description && !isMobile && (
                          <>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary
                              }}
                            >
                              •
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '300px'
                              }}
                            >
                              {template.description}
                            </Typography>
                          </>
                        )}
                        
                      </Box>
                    </Box>
                  </Box>

                  {/* Right Side - Actions Menu */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenTemplateActionMenu(e, template);
                      }}
                      sx={{
                        borderRadius: '6px',
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                          color: theme.palette.text.primary
                        }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        ) : (
          <EmptyState
            icon={<FileText size={22} weight="regular" />}
            title="No templates found"
            description={
              searchQuery
                ? 'Try adjusting your search or filter criteria'
                : statusFilter === 'all'
                ? 'No templates available. Create one to get started'
                : 'No templates in this category yet'
            }
          />
        )}

        <UnifiedTemplateCreationDialog
          open={isTemplateDialogOpen}
          onClose={() => setIsTemplateDialogOpen(false)}
          onCreate={handleCreateTemplate}
          onCreated={(t) => {
            setTemplates((prev) => [t, ...prev]);
            navigate(`/templates/${t.id}/${encodeURIComponent(t.name)}/edit`);
          }}
          categories={templateCategories}
          isLoadingCategories={isLoadingCategories}
        />

        {/* Template Action Menu */}
        <Menu
          anchorEl={templateActionMenuAnchorEl}
          open={Boolean(templateActionMenuAnchorEl)}
          onClose={handleCloseTemplateActionMenu}
          PaperProps={{
            sx: {
              minWidth: 200,
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 4px 20px rgba(0, 0, 0, 0.5)' 
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`
            }
          }}
        >
          <MenuItem
            onClick={() => {
              if (selectedTemplateForAction) {
                navigate(
                  `/templates/${
                    selectedTemplateForAction.id
                  }/${encodeURIComponent(selectedTemplateForAction.name)}/edit`
                );
              }
              handleCloseTemplateActionMenu();
            }}
            sx={{
              py: 1.25,
              px: 2,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
              }
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
              Edit Template
            </ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedTemplateForAction) {
                handleDuplicateTemplate(selectedTemplateForAction);
              }
              handleCloseTemplateActionMenu();
            }}
            disabled={duplicatingTemplateId === selectedTemplateForAction?.id}
            sx={{
              py: 1.25,
              px: 2,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
              }
            }}
          >
            <ListItemIcon>
              {duplicatingTemplateId === selectedTemplateForAction?.id ? (
                <CircularProgress size={16} />
              ) : (
                <CopyIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
              {duplicatingTemplateId === selectedTemplateForAction?.id
                ? 'Duplicating...'
                : 'Duplicate Template'}
            </ListItemText>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              if (selectedTemplateForAction) {
                handleOpenDeleteDialog(selectedTemplateForAction);
              }
              handleCloseTemplateActionMenu();
            }}
            sx={{
              py: 1.25,
              px: 2,
              color: 'error.main',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.08)'
              }
            }}
          >
            <ListItemIcon>
              <DeleteIcon
                fontSize="small"
                sx={{ color: 'error.main' }}
              />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
              Delete Template
            </ListItemText>
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteConfirmDialog}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            {'Delete Template?'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete the template "
              {templateToDelete?.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmDeleteTemplate}
              color="error"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default TemplatesPage;
